import React, { useMemo, useState } from 'react';
import { categoriesApi, uploadApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LoadingSpinner, EmptyState } from './shared';
import { useNavCategories } from '@/context/NavCategoriesContext';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';

function slugify(name) {
  return name
    .toLowerCase()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export const CategoriesSection = ({ categories, loading, onRefresh }) => {
  const { refreshNavCategories } = useNavCategories();

  const syncAfterMutation = () => {
    onRefresh?.();
    void refreshNavCategories();
  };

  const [uploadingId, setUploadingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createKind, setCreateKind] = useState('main');
  const [newCat, setNewCat] = useState({ name: '', slug: '', icon: '', parent_id: '' });
  const [creating, setCreating] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [syncingNavDefaults, setSyncingNavDefaults] = useState(false);

  const mains = useMemo(
    () =>
      categories
        .filter((c) => !c.parent_id)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [categories],
  );

  const childrenOf = (parentId) =>
    categories
      .filter((c) => c.parent_id === parentId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const handleImageUpload = async (categoryId, file) => {
    setUploadingId(categoryId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadApi.uploadImage(formData);
      await categoriesApi.update(categoryId, { image_url: res.data.url });
      toast.success('Kategoribild uppdaterad');
      onRefresh?.();
    } catch {
      toast.error('Kunde inte ladda upp bild');
    } finally {
      setUploadingId(null);
    }
  };

  const openCreateMain = () => {
    setCreateKind('main');
    setNewCat({ name: '', slug: '', icon: '', parent_id: '' });
    setShowCreate(true);
  };

  const openCreateSub = (parentId) => {
    setCreateKind('sub');
    setNewCat({ name: '', slug: '', icon: '', parent_id: parentId });
    setShowCreate(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCat.name || !newCat.slug) {
      toast.error('Namn och slug krävs');
      return;
    }
    if (createKind === 'sub' && !newCat.parent_id) {
      toast.error('Välj huvudkategori för underkategorin');
      return;
    }
    setCreating(true);
    try {
      await categoriesApi.create({
        name: newCat.name,
        slug: newCat.slug,
        icon: newCat.icon || '',
        parent_id: createKind === 'sub' ? newCat.parent_id : null,
      });
      toast.success(createKind === 'sub' ? 'Underkategori skapad' : 'Huvudkategori skapad');
      setShowCreate(false);
      setNewCat({ name: '', slug: '', icon: '', parent_id: '' });
      syncAfterMutation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa kategori');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (cat) => {
    const subs = childrenOf(cat.id);
    const msg =
      subs.length > 0
        ? `Ta bort "${cat.name}" och ${subs.length} underkategor${subs.length === 1 ? 'i' : 'ier'}?`
        : `Ta bort "${cat.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await categoriesApi.delete(cat.id);
      toast.success('Kategori borttagen');
      syncAfterMutation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ta bort kategori');
    }
  };

  const openEdit = (cat) => {
    setEditCat({
      id: cat.id,
      name: cat.name,
      slug: cat.slug,
      icon: cat.icon || '',
      parent_id: cat.parent_id || '',
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editCat?.name || !editCat.slug) {
      toast.error('Namn och slug krävs');
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {
        name: editCat.name,
        slug: editCat.slug,
        icon: editCat.icon || '',
      };
      const isSub = !!categories.find((c) => c.id === editCat.id)?.parent_id;
      if (isSub) {
        payload.parent_id = editCat.parent_id || null;
      }
      await categoriesApi.update(editCat.id, payload);
      toast.success('Kategori uppdaterad');
      setEditCat(null);
      syncAfterMutation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSyncNavDefaults = async () => {
    if (
      !window.confirm(
        'Skapar saknade standardunderkategorier från menykatalogen i databasen. Befintliga kategorier ändras inte. Vill du fortsätta?',
      )
    ) {
      return;
    }
    setSyncingNavDefaults(true);
    try {
      const res = await categoriesApi.syncNavDefaults();
      toast.success(res.data?.message || 'Synk klar');
      syncAfterMutation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte synka underkategorier');
    } finally {
      setSyncingNavDefaults(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6" data-testid="admin-categories">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="font-bold text-[#0F172A]">Kategorier</h3>
          <p className="text-sm text-slate-500 mt-1">
            Huvudkategorier och underkategorier som visas i menyn och vid val av produktkategori. Slug måste vara
            unik.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl border-slate-200"
            disabled={syncingNavDefaults}
            onClick={handleSyncNavDefaults}
            data-testid="sync-nav-defaults-btn"
          >
            {syncingNavDefaults ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Synka meny-underkategorier
          </Button>
          <Button
            onClick={openCreateMain}
            className="bg-[#003B73] hover:bg-[#002a52] rounded-xl"
            data-testid="add-category-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Lägg till huvudkategori
          </Button>
        </div>
      </div>

      {mains.length > 0 ? (
        <div className="space-y-6">
          {mains.map((main) => (
            <div
              key={main.id}
              className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
              data-testid={`category-block-${main.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-stretch gap-0">
                <div className="relative w-full md:w-44 aspect-[4/3] md:aspect-auto md:min-h-[120px] bg-gray-100 shrink-0 group">
                  {main.image_url ? (
                    <img src={main.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl">
                      {main.icon || '📦'}
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    {uploadingId === main.id ? (
                      <Loader2 className="h-6 w-6 text-white animate-spin" />
                    ) : (
                      <ImagePlus className="h-6 w-6 text-white" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && handleImageUpload(main.id, e.target.files[0])}
                    />
                  </label>
                </div>
                <div className="flex-1 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-[#F97316] uppercase tracking-wide">Huvudkategori</p>
                    <p className="font-semibold text-[#0F172A]">{main.name}</p>
                    <p className="text-xs text-gray-400 font-mono">{main.slug}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      onClick={() => openEdit(main)}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Ändra
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      onClick={() => openCreateSub(main.id)}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Underkategori
                    </Button>
                    <button
                      type="button"
                      onClick={() => handleDelete(main)}
                      className="p-2 text-gray-300 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                      title="Ta bort"
                      data-testid={`delete-category-${main.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {childrenOf(main.id).length > 0 && (
                <div className="border-t border-gray-100 bg-slate-50/60 px-4 py-3 space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Underkategorier</p>
                  <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100 bg-white overflow-hidden">
                    {childrenOf(main.id).map((sub) => (
                      <li
                        key={sub.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2.5"
                        data-testid={`subcategory-row-${sub.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden shrink-0 relative group/sub">
                            {sub.image_url ? (
                              <img src={sub.image_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-lg">
                                {sub.icon || '📁'}
                              </div>
                            )}
                            <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover/sub:opacity-100 transition-opacity cursor-pointer">
                              {uploadingId === sub.id ? (
                                <Loader2 className="h-4 w-4 text-white animate-spin" />
                              ) : (
                                <ImagePlus className="h-4 w-4 text-white" />
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) =>
                                  e.target.files[0] && handleImageUpload(sub.id, e.target.files[0])
                                }
                              />
                            </label>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-[#0F172A] truncate">{sub.name}</p>
                            <p className="text-xs text-gray-400 font-mono truncate">{sub.slug}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => openEdit(sub)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <button
                            type="button"
                            onClick={() => handleDelete(sub)}
                            className="p-2 text-gray-300 hover:text-red-500"
                            title="Ta bort"
                            data-testid={`delete-category-${sub.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState message="Inga huvudkategorier hittades" />
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {createKind === 'sub' ? 'Ny underkategori' : 'Ny huvudkategori'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={createKind === 'main' ? 'default' : 'outline'}
                className="flex-1 rounded-xl"
                onClick={() => {
                  setCreateKind('main');
                  setNewCat((n) => ({ ...n, parent_id: '' }));
                }}
              >
                Huvudkategori
              </Button>
              <Button
                type="button"
                variant={createKind === 'sub' ? 'default' : 'outline'}
                className="flex-1 rounded-xl"
                onClick={() => setCreateKind('sub')}
              >
                Underkategori
              </Button>
            </div>
            {createKind === 'sub' && (
              <div>
                <Label>Huvudkategori *</Label>
                <Select
                  value={newCat.parent_id || undefined}
                  onValueChange={(v) => setNewCat((n) => ({ ...n, parent_id: v }))}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Välj huvudkategori" />
                  </SelectTrigger>
                  <SelectContent>
                    {mains.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="cat-name">Namn *</Label>
              <Input
                id="cat-name"
                value={newCat.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setNewCat((n) => {
                    const prevAuto = slugify(n.name);
                    const slugStaysSynced = n.slug === '' || n.slug === prevAuto;
                    return {
                      ...n,
                      name,
                      slug: slugStaysSynced ? slugify(name) : n.slug,
                    };
                  });
                }}
                required
                className="mt-1 rounded-xl"
                placeholder="T.ex. Elverktyg"
                data-testid="new-category-name"
              />
            </div>
            <div>
              <Label htmlFor="cat-slug">Slug *</Label>
              <Input
                id="cat-slug"
                value={newCat.slug}
                onChange={(e) => setNewCat((n) => ({ ...n, slug: e.target.value }))}
                required
                className="mt-1 rounded-xl font-mono text-sm"
                placeholder="t.ex. elverktyg"
                data-testid="new-category-slug"
              />
            </div>
            <div>
              <Label htmlFor="cat-icon">Ikon (emoji, valfritt)</Label>
              <Input
                id="cat-icon"
                value={newCat.icon}
                onChange={(e) => setNewCat((n) => ({ ...n, icon: e.target.value }))}
                className="mt-1 rounded-xl"
                placeholder="🔨"
                data-testid="new-category-icon"
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button" className="rounded-xl">
                  Avbryt
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={creating}
                className="bg-[#003B73] hover:bg-[#002a52] rounded-xl"
                data-testid="save-category-btn"
              >
                {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Skapa
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editCat} onOpenChange={(o) => !o && setEditCat(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Redigera kategori</DialogTitle>
          </DialogHeader>
          {editCat && (
            <form onSubmit={handleSaveEdit} className="space-y-4">
              {categories.find((c) => c.id === editCat.id)?.parent_id ? (
                <div>
                  <Label>Huvudkategori</Label>
                  <Select
                    value={editCat.parent_id || undefined}
                    onValueChange={(v) => setEditCat((e) => ({ ...e, parent_id: v }))}
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue placeholder="Välj huvudkategori" />
                    </SelectTrigger>
                    <SelectContent>
                      {mains.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <div>
                <Label htmlFor="edit-name">Namn *</Label>
                <Input
                  id="edit-name"
                  value={editCat.name}
                  onChange={(e) => setEditCat((e) => ({ ...e, name: e.target.value }))}
                  required
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="edit-slug">Slug *</Label>
                <Input
                  id="edit-slug"
                  value={editCat.slug}
                  onChange={(e) => setEditCat((e) => ({ ...e, slug: e.target.value }))}
                  required
                  className="mt-1 rounded-xl font-mono text-sm"
                />
              </div>
              <div>
                <Label htmlFor="edit-icon">Ikon (emoji)</Label>
                <Input
                  id="edit-icon"
                  value={editCat.icon}
                  onChange={(e) => setEditCat((e) => ({ ...e, icon: e.target.value }))}
                  className="mt-1 rounded-xl"
                />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" type="button" className="rounded-xl">
                    Avbryt
                  </Button>
                </DialogClose>
                <Button type="submit" disabled={savingEdit} className="bg-[#003B73] hover:bg-[#002a52] rounded-xl">
                  {savingEdit && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Spara
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
